/**
 * Hotjar (session recording / heatmaps) loader. Mirrors the vendor's IIFE snippet, parameterized by
 * site id so each environment uses its own. Called once at app bootstrap from `main.tsx`, gated on
 * `VITE_HOTJAR_ID` — which is set only in `.env.dev` / `.env.prod`, so local dev never loads Hotjar.
 */

declare global {
  interface Window {
    hj?: ((...args: unknown[]) => void) & { q?: unknown[] };
    _hjSettings?: { hjid: number; hjsv: number };
  }
}

/**
 * Inject the Hotjar tag for the given site id. Idempotent — a no-op once `window.hj` exists.
 * Emits `[hotjar]` console diagnostics in EVERY env (prod included) so you can confirm in the
 * browser console whether the tag actually loaded — a tag that fails to load vs. a loaded-but-no-
 * widget site are different problems. A missing Feedback/survey button is almost always the latter:
 * widgets are configured per-site in the Hotjar dashboard, NOT in this code (e.g. 2026-07: the
 * Feedback widget existed on the dev site 6494351 but not on the prod site 6433715).
 */
export function initHotjar(hjid: string, hjsv = 6): void {
  if (typeof window === 'undefined') return;
  if (window.hj) { console.info('[hotjar] already initialised, skipping'); return; }
  window.hj = window.hj || function (...args: unknown[]) {
    (window.hj!.q = window.hj!.q || []).push(args);
  };
  window._hjSettings = { hjid: Number(hjid), hjsv };
  const src = `https://static.hotjar.com/c/hotjar-${hjid}.js?sv=${hjsv}`;
  console.info(`[hotjar] init: site ${hjid} (sv ${hjsv}) → ${src}`);
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  script.onload = () => console.info(`[hotjar] tag loaded for site ${hjid}. Widgets (Feedback/surveys) are configured per-site in the Hotjar dashboard. If none show, the script is fine but no widget is published for this site.`);
  script.onerror = () => console.error(`[hotjar] tag FAILED to load: ${src} (network blocked, ad-blocker, or bad site id?)`);
  document.head.appendChild(script);
}
